import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Row, Col, DatePicker, Statistic, Table, Tag, Spin, Tabs, App } from 'antd';
import { ShoppingCartOutlined, FileExcelOutlined, BarChartOutlined, TruckOutlined, CheckCircleOutlined, UserOutlined, WarningOutlined, BankOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { AUTH_USER_CHANGED_EVENT, authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import dayjs from 'dayjs';
import IntrastatExport from '../components/IntrastatExport';
import {
  AtRiskOrdersReportDto,
  CostsPaymentStatusReportDto,
  GoodsInTransitReportDto,
  MonthlyProfitReportDto,
  SalesByUserDto,
  SettlementsReportDto,
  normalizeAtRiskOrdersReport,
  normalizeCostsPaymentStatusReport,
  normalizeGoodsInTransitReport,
  normalizeMonthlyProfitReport,
  normalizeSalesByUser,
  normalizeSettlementsReport,
} from '../dto/report';
import { REPORTS_TAB_IDS, getReportsTabDefinition } from '../modules/reports/reportTabs';

const { MonthPicker } = DatePicker;
const monthlyReportsTab = getReportsTabDefinition(REPORTS_TAB_IDS.monthlyReports);
const goodsInTransitTab = getReportsTabDefinition(REPORTS_TAB_IDS.goodsInTransit);
const costsPaymentStatusTab = getReportsTabDefinition(REPORTS_TAB_IDS.costsPaymentStatus);
const intrastatExportTab = getReportsTabDefinition(REPORTS_TAB_IDS.intrastatExport);
const salesByUserTab = getReportsTabDefinition(REPORTS_TAB_IDS.salesByUser);
const atRiskOrdersTab = getReportsTabDefinition(REPORTS_TAB_IDS.atRiskOrders);
const settlementsTab = getReportsTabDefinition(REPORTS_TAB_IDS.settlements);

const Reports: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'common', 'orders']);
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<MonthlyProfitReportDto | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [goodsInTransit, setGoodsInTransit] = useState<GoodsInTransitReportDto | null>(null);
  const [transitLoading, setTransitLoading] = useState(false);
  const [costsPaymentData, setCostsPaymentData] = useState<CostsPaymentStatusReportDto | null>(null);
  const [costsPaymentLoading, setCostsPaymentLoading] = useState(false);
  const [salesByUser, setSalesByUser] = useState<SalesByUserDto[]>([]);
  const [salesByUserLoading, setSalesByUserLoading] = useState(false);
  const [atRiskOrders, setAtRiskOrders] = useState<AtRiskOrdersReportDto | null>(null);
  const [atRiskLoading, setAtRiskLoading] = useState(false);
  const [settlements, setSettlements] = useState<SettlementsReportDto | null>(null);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [, setAuthRevision] = useState(0);
  const canViewFinancials = authService.canViewFinancials();
  const locale = i18n.language.startsWith('pl') ? 'pl-PL' : 'en-US';
  const formatReportAmount = (value: string | number | undefined) => Number(value || 0).toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  useEffect(() => {
    const handleAuthUserChanged = () => setAuthRevision((revision) => revision + 1);
    window.addEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, handleAuthUserChanged);
  }, []);

  useEffect(() => {
    if (!canViewFinancials) return;
    fetchMonthlyReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, canViewFinancials]);

  const fetchGoodsInTransit = async () => {
    setTransitLoading(true);
    try {
      const response = await api.get('/reports/goods-in-transit');
      setGoodsInTransit(normalizeGoodsInTransitReport(response.data));
    } catch (error) {
      console.error('Failed to fetch goods in transit:', error);
      message.error(t('reports:goodsInTransit.fetchFailed'));
    } finally {
      setTransitLoading(false);
    }
  };

  const fetchCostsPaymentStatus = async () => {
    setCostsPaymentLoading(true);
    try {
      const response = await api.get('/reports/costs-payment-status');
      setCostsPaymentData(normalizeCostsPaymentStatusReport(response.data));
    } catch (error) {
      console.error('Failed to fetch costs payment status:', error);
      message.error(t('reports:costsPayment.fetchFailed'));
    } finally {
      setCostsPaymentLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    setLoading(true);
    try {
      const response = await api.get('/reports/monthly-profit', {
        params: {
          year: selectedMonth.year(),
          month: selectedMonth.month() + 1
        }
      });
      setReportData(normalizeMonthlyProfitReport(response.data));
    } catch (error) {
      console.error('Failed to fetch report:', error);
      message.error(t('reports:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSalesByUser = async () => {
    setSalesByUserLoading(true);
    try {
      const response = await api.get('/reports/sales-by-user');
      setSalesByUser(normalizeSalesByUser(response.data));
    } catch (error) {
      console.error('Failed to fetch sales by user:', error);
      message.error(t('reports:salesByUser.fetchFailed'));
    } finally {
      setSalesByUserLoading(false);
    }
  };

  const fetchAtRiskOrders = async () => {
    setAtRiskLoading(true);
    try {
      const response = await api.get('/reports/at-risk-orders');
      setAtRiskOrders(normalizeAtRiskOrdersReport(response.data));
    } catch (error) {
      console.error('Failed to fetch at-risk orders:', error);
      message.error(t('reports:atRisk.fetchFailed'));
    } finally {
      setAtRiskLoading(false);
    }
  };

  const fetchSettlements = async () => {
    setSettlementsLoading(true);
    try {
      const response = await api.get('/reports/settlements');
      setSettlements(normalizeSettlementsReport(response.data));
    } catch (error) {
      console.error('Failed to fetch settlements:', error);
      message.error(t('reports:settlements.fetchFailed'));
    } finally {
      setSettlementsLoading(false);
    }
  };

  const orderColumns = [
    {
      title: t('reports:monthlyReport.table.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (value: string, record: { id: string; displayOrderNumber?: string }) => (
        <Button type="link" style={{ padding: 0 }} onClick={() => window.location.href = `/app/orders/${record.id}`}>
          {record.displayOrderNumber || value}
        </Button>
      ),
    },
    {
      title: t('reports:monthlyReport.table.customer'),
      dataIndex: 'customerName',
      key: 'customer',
    },
    {
      title: t('reports:monthlyReport.table.revenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      render: (value: number) => formatCurrency(value, 'PLN'),
    },
    {
      title: t('reports:monthlyReport.table.totalCost'),
      dataIndex: 'totalCost',
      key: 'totalCost',
      render: (value: number) => formatCurrency(value, 'PLN'),
    },
    {
      title: t('reports:monthlyReport.table.margin'),
      dataIndex: 'margin',
      key: 'margin',
      render: (value: number, record: { marginPercent: number }) => {
        const marginValue = value != null ? value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00';
        const marginPercent = record.marginPercent != null ? record.marginPercent.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '0,0';
        const color = (record.marginPercent ?? 0) < 15 ? 'red' : 'green';
        return (
          <span style={{ color }}>
            {marginValue} PLN ({marginPercent}%)
          </span>
        );
      },
    },
    {
      title: t('reports:monthlyReport.table.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'DELIVERED' ? 'success' : 'processing'}>
          {t(`orders:statusOptions.${status}`, { defaultValue: status.replace(/_/g, ' ') })}
        </Tag>
      ),
    },
  ];

  const tabItems = [
    {
      key: monthlyReportsTab.key,
      label: (
        <span>
          <BarChartOutlined />
          {t(monthlyReportsTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between' }}>
            <h3>{t('reports:monthlyReport.title')}</h3>
            <MonthPicker
              value={selectedMonth}
              onChange={(date) => date && setSelectedMonth(date)}
            />
          </div>

          {loading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : (
            reportData && (
              <>
                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col xs={24} md={12} xl={6}>
                    <Card>
                      <Statistic
                        title={t('reports:monthlyReport.totalOrders')}
                        value={reportData.summary.totalOrders}
                        prefix={<ShoppingCartOutlined />}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <Card>
                      <Statistic
                        title={t('reports:monthlyReport.totalRevenue')}
                        value={reportData.summary.totalRevenue}
                        formatter={formatReportAmount}
                        suffix="PLN"
                        valueStyle={{ color: '#3f8600' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <Card>
                      <Statistic
                        title={t('reports:monthlyReport.totalCosts')}
                        value={reportData.summary.totalCosts}
                        formatter={formatReportAmount}
                        suffix="PLN"
                        valueStyle={{ color: '#cf1322' }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12} xl={6}>
                    <Card>
                      <Statistic
                        title={t('reports:monthlyReport.netProfit')}
                        value={reportData.summary.totalMargin}
                        formatter={formatReportAmount}
                        valueStyle={{
                          color: reportData.summary.totalMargin >= 0 ? '#3f8600' : '#cf1322'
                        }}
                        suffix={
                          <span>
                            PLN
                            {reportData.summary.averageMarginPercent != null && (
                              <span style={{ fontSize: 14, marginLeft: 8 }}>
                                ({formatLocalizedPercent(reportData.summary.averageMarginPercent, i18n.language)})
                              </span>
                            )}
                          </span>
                        }
                      />
                    </Card>
                  </Col>
                </Row>

                <Row gutter={16} style={{ marginBottom: 24 }}>
                  <Col span={12}>
                    <Card title={t('reports:monthlyReport.ordersByStatus')}>
                      {reportData.summary.ordersByStatus ? 
                        Object.entries(reportData.summary.ordersByStatus).map(([status, count]) => (
                          <div key={status} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{t(`orders:statusOptions.${status}`, { defaultValue: status.replace(/_/g, ' ') })}</span>
                            <strong>{count as number}</strong>
                          </div>
                        )) : 
                        <div>{t('reports:noData')}</div>
                      }
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title={t('reports:monthlyReport.costsByCategory')}>
                      {reportData.summary.costsByCategory ? 
                        Object.entries(reportData.summary.costsByCategory).map(([category, amount]) => (
                          <div key={category} style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                            <span>{t(`orders:costCategories.${category}`, { defaultValue: category.replace(/_/g, ' ') })}</span>
                            <strong>{formatCurrency(amount as number, 'PLN')}</strong>
                          </div>
                        )) :
                        <div>{t('reports:noData')}</div>
                      }
                    </Card>
                  </Col>
                </Row>

                <Card title={t('reports:monthlyReport.orderDetails')}>
                  <Table
                    dataSource={reportData.orders}
                    columns={orderColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    summary={(data) => (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} colSpan={2}>
                          <strong>{t('reports:monthlyReport.total')}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2}>
                        <strong>{formatCurrency(data.reduce((sum: number, order) => sum + (order.revenue ?? 0), 0))}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3}>
                        <strong>{formatCurrency(data.reduce((sum: number, order) => sum + (order.totalCost ?? 0), 0))}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4}>
                        <strong>{formatCurrency(data.reduce((sum: number, order) => sum + (order.margin ?? 0), 0))}</strong>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={5} />
                      </Table.Summary.Row>
                    )}
                  />
                </Card>
              </>
            )
          )}
        </div>
      ),
    },
    {
      key: goodsInTransitTab.key,
      label: (
        <span>
          <TruckOutlined />
          {t(goodsInTransitTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          {transitLoading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : goodsInTransit ? (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={t('reports:goodsInTransit.ordersInTransit')}
                      value={goodsInTransit.summary.totalOrders}
                      prefix={<TruckOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={t('reports:goodsInTransit.totalProducts')}
                      value={goodsInTransit.summary.totalProducts}
                      prefix={<ShoppingCartOutlined />}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={t('reports:goodsInTransit.totalValue')}
                      value={goodsInTransit.summary.totalValue}
                      precision={2}
                      suffix={goodsInTransit.summary.totalValueCurrency}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={t('reports:goodsInTransit.totalWeight')}
                      value={goodsInTransit.summary.totalWeight}
                      precision={2}
                    />
                  </Card>
                </Col>
              </Row>

              <Card title={t('reports:goodsInTransit.ordersInTransit')}>
                <Table<GoodsInTransitReportDto['orders'][number]>
                  dataSource={goodsInTransit.orders}
                  rowKey="id"
                  columns={[
                    {
                      title: t('reports:goodsInTransit.table.orderNumber'),
                      dataIndex: 'orderNumber',
                      key: 'orderNumber',
                      render: (value: string, record: { id: string; displayOrderNumber?: string }) => (
                        <Button type="link" style={{ padding: 0 }} href={`/app/orders/${record.id}`}>
                          {record.displayOrderNumber || value}
                        </Button>
                      ),
                    },
                    { title: t('reports:goodsInTransit.table.customer'), dataIndex: 'customerName', key: 'customer' },
                    { title: t('reports:goodsInTransit.table.supplier'), dataIndex: 'supplierName', key: 'supplier', render: (v: string) => v || '-' },
                    { title: t('reports:goodsInTransit.table.expectedDelivery'), dataIndex: 'expectedDelivery', key: 'expectedDelivery', render: (d: string) => d ? dayjs(d).format('YYYY-MM-DD') : '-' },
                    { title: t('reports:goodsInTransit.table.destination'), dataIndex: 'deliveryLocation', key: 'deliveryLocation', render: (v: string) => v || '-' },
                    { title: t('reports:goodsInTransit.table.value'), dataIndex: 'revenue', key: 'revenue', render: (v: number) => formatCurrency(v, 'PLN') },
                  ]}
                  expandable={{
                    expandedRowRender: (record: GoodsInTransitReportDto['orders'][number]) => (
                      <Table
                        dataSource={record.products}
                        rowKey="id"
                        pagination={false}
                        size="small"
                        columns={[
                          { title: t('reports:goodsInTransit.table.product'), dataIndex: 'name', key: 'name' },
                          { title: t('reports:goodsInTransit.table.quantity'), dataIndex: 'quantity', key: 'quantity' },
                          { title: t('orders:orderDetail.unitPrice'), dataIndex: 'unitPrice', key: 'unitPrice', render: (v: number, product: { currency?: string }) => v == null ? '-' : formatCurrency(v, product.currency || 'PLN') },
                          { title: t('orders:orderDetail.saleTotal'), dataIndex: 'totalPrice', key: 'totalPrice', render: (v: number, product: { currency?: string }) => v == null ? '-' : formatCurrency(v, product.currency || 'PLN') },
                          { title: t('reports:goodsInTransit.table.cnCode'), dataIndex: 'cnCode', key: 'cnCode', render: (v: string) => v || '-' },
                          { title: t('reports:goodsInTransit.table.weight'), dataIndex: 'grossWeight', key: 'grossWeight', render: (v: number) => v || '-' },
                        ]}
                      />
                    ),
                  }}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{t('reports:goodsInTransit.clickToLoad')}</p>
                <button onClick={fetchGoodsInTransit}>{t('reports:goodsInTransit.loadReport')}</button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: costsPaymentStatusTab.key,
      label: (
        <span>
          <CheckCircleOutlined />
          {t(costsPaymentStatusTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          {costsPaymentLoading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : costsPaymentData ? (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={`${t('reports:costsPayment.totalCosts')} (${costsPaymentData.summary.totalCount})`}
                      value={costsPaymentData.summary.totalAmount}
                      precision={2}
                      suffix="PLN"
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={`${t('reports:costsPayment.paid')} (${costsPaymentData.summary.paidCount})`}
                      value={costsPaymentData.summary.paidAmount}
                      precision={2}
                      valueStyle={{ color: '#3f8600' }}
                      suffix="PLN"
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={`${t('reports:costsPayment.unpaid')} (${costsPaymentData.summary.unpaidCount})`}
                      value={costsPaymentData.summary.unpaidAmount}
                      precision={2}
                      valueStyle={{ color: '#cf1322' }}
                      suffix="PLN"
                    />
                  </Card>
                </Col>
                <Col span={6}>
                  <Card>
                    <Statistic
                      title={t('reports:costsPayment.totalAmount')}
                      value={costsPaymentData.summary.totalAmount}
                      precision={2}
                      suffix="PLN"
                    />
                  </Card>
                </Col>
              </Row>

              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                  <Card title={t('reports:costsPayment.byCategory')}>
                    <Table
                      dataSource={costsPaymentData.byCategory}
                      rowKey="category"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: t('reports:costsPayment.table.category'),
                          dataIndex: 'category',
                          key: 'category',
                          render: (v: string) => t(`orders:costCategories.${v}`, { defaultValue: v.replace(/_/g, ' ') }),
                        },
                        {
                          title: t('reports:costsPayment.paidSuffix'),
                          dataIndex: 'paidAmount',
                          key: 'paidAmount',
                          render: (v: number) => <span style={{ color: '#52c41a' }}>{formatCurrency(v)}</span>,
                        },
                        {
                          title: t('reports:costsPayment.unpaidSuffix'),
                          dataIndex: 'unpaidAmount',
                          key: 'unpaidAmount',
                          render: (v: number) => <span style={{ color: '#ff4d4f' }}>{formatCurrency(v)}</span>,
                        },
                      ]}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card title={t('reports:costsPayment.topUnpaidCosts')}>
                    <Table
                      dataSource={costsPaymentData.unpaidCosts.slice(0, 10)}
                      rowKey="id"
                      pagination={false}
                      size="small"
                      columns={[
                        {
                          title: t('reports:costsPayment.table.order'),
                          dataIndex: 'orderNumber',
                          key: 'orderNumber',
                          render: (value: string, record: { displayOrderNumber?: string }) => record.displayOrderNumber || value,
                        },
                        { title: t('reports:costsPayment.table.category'), dataIndex: 'category', key: 'category', render: (v: string) => t(`orders:costCategories.${v}`, { defaultValue: v.replace(/_/g, ' ') }) },
                        { title: t('reports:costsPayment.table.amount'), dataIndex: 'amount', key: 'amount', render: (v: number) => formatCurrency(v) },
                      ]}
                    />
                  </Card>
                </Col>
              </Row>
            </>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{t('reports:costsPayment.clickToLoad')}</p>
                <button onClick={fetchCostsPaymentStatus}>{t('reports:costsPayment.loadReport')}</button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: intrastatExportTab.key,
      label: (
        <span>
          <FileExcelOutlined />
          {t(intrastatExportTab.labelKey)}
        </span>
      ),
      children: <IntrastatExport />,
    },
    {
      key: salesByUserTab.key,
      label: (
        <span>
          <UserOutlined />
          {t(salesByUserTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          {salesByUserLoading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : salesByUser.length > 0 ? (
            <Card title={t('reports:salesByUser.title')}>
              <Table
                dataSource={salesByUser}
                rowKey="userId"
                columns={[
                  { title: '#', key: 'rank', render: (_: any, __: any, index: number) => index + 1 },
                  { title: t('reports:salesByUser.userName'), dataIndex: 'userName', key: 'userName' },
                  { title: t('reports:salesByUser.orderCount'), dataIndex: 'orderCount', key: 'orderCount' },
                  { title: t('reports:salesByUser.totalRevenue'), dataIndex: 'totalRevenue', key: 'totalRevenue', render: (v: number) => formatCurrency(v) },
                  { title: t('reports:salesByUser.totalCost'), dataIndex: 'totalCost', key: 'totalCost', render: (v: number) => formatCurrency(v) },
                  { title: t('reports:salesByUser.totalMargin'), dataIndex: 'totalMargin', key: 'totalMargin', render: (v: number) => (
                    <span style={{ color: v >= 0 ? 'green' : 'red' }}>{formatCurrency(v)}</span>
                  )},
                  { title: t('reports:salesByUser.avgMargin'), dataIndex: 'avgMarginPercent', key: 'avgMarginPercent', render: (v: number) => (
                    <span style={{ color: v >= 15 ? 'green' : 'red' }}>{formatLocalizedPercent(v, i18n.language)}</span>
                  )},
                ]}
                pagination={false}
              />
            </Card>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{t('reports:salesByUser.clickToLoad')}</p>
                <button onClick={fetchSalesByUser}>{t('reports:salesByUser.loadReport')}</button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: atRiskOrdersTab.key,
      label: (
        <span>
          <WarningOutlined />
          {t(atRiskOrdersTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          {atRiskLoading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : atRiskOrders ? (
            <>
              <Alert
                type="warning"
                message={t('reports:atRisk.thresholdNote', { threshold: atRiskOrders.threshold })}
                style={{ marginBottom: 16 }}
              />
              <Card title={t('reports:atRisk.title')}>
                <Table
                  dataSource={atRiskOrders.orders}
                  rowKey="id"
                  columns={[
                    {
                      title: t('reports:atRisk.table.orderNumber'),
                      dataIndex: 'orderNumber',
                      key: 'orderNumber',
                      render: (value: string, record: { id: string; displayOrderNumber?: string }) => {
                        const orderHref = `/app/orders/${record.id}`;

                        return (
                          <Button type="link" style={{ padding: 0 }} href={orderHref}>
                            {record.displayOrderNumber || value}
                          </Button>
                        );
                      },
                    },
                    { title: t('reports:atRisk.table.customer'), dataIndex: 'customerName', key: 'customer' },
                    { title: t('reports:atRisk.table.status'), dataIndex: 'status', key: 'status', render: (s: string) => (
                      <Tag color="warning">{t(`orders:statusOptions.${s}`, { defaultValue: s.replace(/_/g, ' ') })}</Tag>
                    )},
                    { title: t('reports:atRisk.table.revenue'), dataIndex: 'revenue', key: 'revenue', render: (v: number) => formatCurrency(v) },
                    { title: t('reports:atRisk.table.margin'), dataIndex: 'marginPercent', key: 'marginPercent', render: (v: number) => (
                      <span style={{ color: 'red', fontWeight: 500 }}>{formatLocalizedPercent(v ?? 0, i18n.language)}</span>
                    )},
                  ]}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{t('reports:atRisk.clickToLoad')}</p>
                <button onClick={fetchAtRiskOrders}>{t('reports:atRisk.loadReport')}</button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: settlementsTab.key,
      label: (
        <span>
          <BankOutlined />
          {t(settlementsTab.labelKey)}
        </span>
      ),
      children: (
        <div>
          {settlementsLoading ? (
            <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
          ) : settlements ? (
            <>
              <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title={t('reports:settlements.avgDeviation')}
                      value={settlements.summary.avgDeviation}
                      precision={2}
                      suffix="%"
                      valueStyle={{ color: settlements.summary.avgDeviation >= 0 ? '#3f8600' : '#cf1322' }}
                    />
                  </Card>
                </Col>
                <Col span={12}>
                  <Card>
                    <Statistic
                      title={t('reports:settlements.negativeDeviationPercent')}
                      value={settlements.summary.negativeDeviationPercent}
                      precision={1}
                      suffix="%"
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Card>
                </Col>
              </Row>
              <Card title={t('reports:settlements.title')}>
                <Table
                  dataSource={settlements.orders}
                  rowKey="id"
                  columns={[
                    {
                      title: t('reports:settlements.table.orderNumber'),
                      dataIndex: 'orderNumber',
                      key: 'orderNumber',
                      render: (value: string, record: { id: string; displayOrderNumber?: string }) => (
                        <Button type="link" style={{ padding: 0 }} onClick={() => window.location.href = `/app/orders/${record.id}`}>
                          {record.displayOrderNumber || value}
                        </Button>
                      ),
                    },
                    { title: t('reports:settlements.table.customer'), dataIndex: 'customerName', key: 'customer' },
                    { title: t('reports:settlements.table.plannedMargin'), dataIndex: 'plannedMarginPercent', key: 'plannedMarginPercent', render: (v: number) => (
                      <span style={{ color: v >= 15 ? 'green' : 'red' }}>{formatLocalizedPercent(v ?? 0, i18n.language)}</span>
                    )},
                    { title: t('reports:settlements.table.settledMargin'), dataIndex: 'settledMarginPercent', key: 'settledMarginPercent', render: (v: number) => (
                      <span style={{ color: v >= 15 ? 'green' : 'red' }}>{formatLocalizedPercent(v ?? 0, i18n.language)}</span>
                    )},
                    { title: t('reports:settlements.table.deviation'), dataIndex: 'deviationPercent', key: 'deviationPercent', render: (v: number) => (
                      <span style={{ color: v >= 0 ? 'green' : 'red' }}>{formatLocalizedPercent(v ?? 0, i18n.language)}</span>
                    )},
                  ]}
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </>
          ) : (
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <p>{t('reports:settlements.clickToLoad')}</p>
                <button onClick={fetchSettlements}>{t('reports:settlements.loadReport')}</button>
              </div>
            </Card>
          )}
        </div>
      ),
    },
  ];

  if (!canViewFinancials) {
    return <Alert message={t('common:accessDenied', { defaultValue: 'Access denied' })} type="error" />;
  }

  return (
    <div>
      <h2>{t('reports:title')}</h2>
      <Tabs defaultActiveKey={monthlyReportsTab.key} items={tabItems} />
    </div>
  );
};

export default Reports;
